// src/services/classMetrics.js
// Thin client wrapper around GET /api/admin-class-metrics.
// Admin-only — relies on the Clerk getToken passed in by the caller.
//
// See docs/decisions/adr-class-metrics.md for the response shape and
// design rationale.

// ── In-session cache (2026-05-04) ──
// Admin reopens the same class's metrics frequently (browse → close → check
// another tab → reopen → click Refresh). Without a cache, each open hits the
// full pipeline: Clerk JWT verify → 6 parallel Firestore queries → Clerk bulk
// REST lookup. That's ~500ms-2s per open. Caching by classId for 30 seconds
// makes repeat opens instant while keeping the data acceptably fresh.
//
// Cache scope: page lifetime (module scope, cleared on full reload).
// Cache invalidation: explicit via `force: true` (Refresh button) or
// `clearMetricsCache(classId)` after a known-mutating admin action.
// 30s TTL: long enough to cover the close→reopen pattern, short enough that
// stale data is bounded. Admin is the only caller, so cross-user staleness
// is not a concern.
const CACHE_TTL_MS = 30 * 1000;
const cache = new Map(); // classId → { data, timestamp }

// Sync cache reader. Used by the modal to seed initial state without an
// awkward async loading flicker on cache hits (the modal can render data
// immediately if cache is fresh, instead of going skeleton → data).
// Returns null on miss or expiry.
export function getCachedMetrics(classId) {
    if (!classId) return null;
    const entry = cache.get(classId);
    if (!entry) return null;
    if (Date.now() - entry.timestamp >= CACHE_TTL_MS) {
        cache.delete(classId); // best-effort eviction; not strictly required
        return null;
    }
    return entry.data;
}

// Drop the cache entry for a class (or all entries when no id). Call after an
// admin action known to mutate underlying data — currently no such caller
// exists, but exposing the helper avoids surprises if/when one does (e.g.,
// admin deletes a comment and immediately reopens metrics).
export function clearMetricsCache(classId) {
    if (classId) cache.delete(classId);
    else cache.clear();
}

// ── Fetch metrics for a single class ──
// Returns the full metrics payload (see ADR §3) or throws an Error with the
// HTTP status / server message on failure. Caller is responsible for
// rendering loading/error states.
//
// Options:
//   force: true → bypass cache, fetch fresh, update cache.
//   force: false (default) → return cached if fresh, else fetch.
export async function fetchClassMetrics(classId, getToken, options = {}) {
    if (!classId) throw new Error('classId is required');
    if (typeof getToken !== 'function') throw new Error('getToken is required');
    const { force = false } = options;

    if (!force) {
        const cached = getCachedMetrics(classId);
        if (cached) return cached;
    }

    const token = await getToken();
    const res = await fetch(`/api/admin-class-metrics?classId=${encodeURIComponent(classId)}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    if (!res.ok) {
        let detail = '';
        try { detail = (await res.json())?.error || ''; } catch (_) { /* defensive */ }
        throw new Error(detail || `HTTP ${res.status}`);
    }
    const data = await res.json();
    cache.set(classId, { data, timestamp: Date.now() });
    return data;
}
